export const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

export const DEFAULT_ADMIN_API_BASE_URL = 'http://localhost:3010';
export const DEFAULT_TENANT_API_BASE_URL = 'http://localhost:3012';
export const DEFAULT_TENANT_FRONTEND_ORIGIN = 'http://localhost:3013';
export const DEFAULT_TENANT_BASE_DOMAIN = 'duan.xiaoniuweibang.com';
export const DEFAULT_SHORT_LINK_BASE_URL = 'https://duan.xiaoniuweibang.com';
export const DEFAULT_ADMIN_HOSTS = '';

const LOCAL_ADMIN_API_PORT_BY_FRONTEND_PORT: Record<string, string> = {
  '3011': '3010',
  '3111': '3110',
};

const LOCAL_TENANT_API_PORT_BY_FRONTEND_PORT: Record<string, string> = {
  '3011': '3012',
  '3013': '3012',
  '3111': '3112',
  '3113': '3112',
};

const readEnvValue = (key: string, fallback: string) => {
  const value = process.env[key];
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
};

const getLocation = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.location;
};

const getCurrentOrigin = () => {
  const location = getLocation();
  return location?.origin?.trim() || '';
};

const getTenantApiEnvBaseUrl = () =>
  process.env.REACT_APP_TENANT_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || '';

const rewriteLocalDevHostname = (value: string) => {
  const location = getLocation();
  if (!location) {
    return value;
  }

  const currentHostname = location.hostname.trim().toLowerCase();
  if (!LOCAL_HOSTS.has(currentHostname)) {
    return value;
  }

  try {
    const parsed = new URL(value);
    const targetHostname = parsed.hostname.trim().toLowerCase();
    if (!LOCAL_HOSTS.has(targetHostname) || targetHostname === currentHostname) {
      return value;
    }

    parsed.hostname = currentHostname;
    return parsed.toString().replace(/\/$/, '');
  } catch {
    return value;
  }
};

const getLocalMappedApiBaseUrl = (tenant: boolean) => {
  const location = getLocation();
  if (!location) {
    return tenant ? DEFAULT_TENANT_API_BASE_URL : DEFAULT_ADMIN_API_BASE_URL;
  }

  const frontendPort = location.port.trim();
  const mappedPort = tenant
    ? LOCAL_TENANT_API_PORT_BY_FRONTEND_PORT[frontendPort]
    : LOCAL_ADMIN_API_PORT_BY_FRONTEND_PORT[frontendPort];

  if (mappedPort) {
    return `${location.protocol}//${location.hostname}:${mappedPort}`;
  }

  return rewriteLocalDevHostname(tenant ? DEFAULT_TENANT_API_BASE_URL : DEFAULT_ADMIN_API_BASE_URL);
};

export const getTenantBaseDomain = () =>
  readEnvValue('REACT_APP_TENANT_BASE_DOMAIN', DEFAULT_TENANT_BASE_DOMAIN).toLowerCase();

export const getShortLinkBaseUrl = () =>
  readEnvValue('REACT_APP_SHORT_LINK_BASE_URL', DEFAULT_SHORT_LINK_BASE_URL);

export const getAdminHosts = () =>
  readEnvValue('REACT_APP_ADMIN_HOSTS', DEFAULT_ADMIN_HOSTS)
    .split(',')
    .map((host) => host.trim().toLowerCase())
    .filter(Boolean);

export const isLocalDevelopmentHost = (hostname?: string | null) => {
  const resolvedHostname = (hostname ?? getLocation()?.hostname ?? '').trim().toLowerCase();
  return LOCAL_HOSTS.has(resolvedHostname);
};

export const isTenantRuntime = () => {
  const location = getLocation();
  if (!location) {
    return false;
  }

  const { hostname, port, pathname } = location;
  const baseDomain = getTenantBaseDomain();
  const normalizedHost = hostname.trim().toLowerCase();
  const adminHosts = getAdminHosts();

  if (port === '3013') {
    return true;
  }

  if (/^\/tenant(\/|$)/.test(pathname)) {
    return true;
  }

  if (adminHosts.includes(normalizedHost)) {
    return false;
  }

  if (LOCAL_HOSTS.has(normalizedHost) || normalizedHost === baseDomain) {
    return false;
  }

  if (normalizedHost.endsWith(`.${baseDomain}`)) {
    return true;
  }

  return true;
};

export const getTenantFrontendOrigin = (subdomain?: string | null) => {
  const location = getLocation();

  if (!location || isLocalDevelopmentHost(location.hostname)) {
    return rewriteLocalDevHostname(DEFAULT_TENANT_FRONTEND_ORIGIN);
  }

  const protocol = location.protocol === 'https:' ? 'https:' : 'http:';

  if (subdomain) {
    return `${protocol}//${subdomain}.${getTenantBaseDomain()}`;
  }

  return `${protocol}//${getTenantBaseDomain()}`;
};

export const getAdminApiBaseUrl = () => {
  if (isLocalDevelopmentHost()) {
    return getLocalMappedApiBaseUrl(false);
  }

  if (process.env.REACT_APP_API_BASE_URL && !isTenantRuntime()) {
    return rewriteLocalDevHostname(process.env.REACT_APP_API_BASE_URL);
  }

  if (process.env.NODE_ENV === 'production') {
    const currentOrigin = getCurrentOrigin();
    return currentOrigin ? `${currentOrigin}/api` : '/api';
  }

  return rewriteLocalDevHostname(DEFAULT_ADMIN_API_BASE_URL);
};

export const getTenantApiBaseUrl = () => {
  const location = getLocation();
  const tenantApiEnvBaseUrl = getTenantApiEnvBaseUrl();

  if (isLocalDevelopmentHost(location?.hostname)) {
    const tenantRuntime =
      location?.port === '3013' ||
      location?.port === '3113' ||
      /^\/tenant(\/|$)/.test(location?.pathname || '');

    return getLocalMappedApiBaseUrl(tenantRuntime);
  }

  if (
    tenantApiEnvBaseUrl &&
    process.env.NODE_ENV !== 'production' &&
    (location?.port === '3013' || /^\/tenant(\/|$)/.test(location?.pathname || ''))
  ) {
    return rewriteLocalDevHostname(tenantApiEnvBaseUrl);
  }

  if (process.env.NODE_ENV === 'production') {
    const currentOrigin = getCurrentOrigin();
    return currentOrigin ? `${currentOrigin}/api` : '/api';
  }

  if (!location) {
    return DEFAULT_TENANT_API_BASE_URL;
  }

  const normalizedHost = location.hostname.trim().toLowerCase();
  const baseDomain = getTenantBaseDomain();
  const adminHosts = getAdminHosts();
  const tenantRuntime =
    location.port === '3013' ||
    /^\/tenant(\/|$)/.test(location.pathname) ||
    (!adminHosts.includes(normalizedHost) &&
      !LOCAL_HOSTS.has(normalizedHost) &&
      normalizedHost !== baseDomain);

  return rewriteLocalDevHostname(
    tenantRuntime ? DEFAULT_TENANT_API_BASE_URL : DEFAULT_ADMIN_API_BASE_URL,
  );
};

export const getApiBaseUrl = () => {
  return isTenantRuntime() ? getTenantApiBaseUrl() : getAdminApiBaseUrl();
};
